import { z } from 'zod/v3';

// ============================================
// Integration Config Types (Authentication)
// ============================================

/**
 * GitHub OAuth configuration
 */
export const GitHubConfigSchema = z.object({
  token: z.string(),
  refreshToken: z.string().optional(),
  expiresAt: z.number().optional(),
  username: z.string().optional(),
  userId: z.string().optional(),
});
export type GitHubConfig = z.infer<typeof GitHubConfigSchema>;

/**
 * Bitbucket OAuth configuration
 */
export const BitbucketConfigSchema = z.object({
  token: z.string(),
  refreshToken: z.string().optional(),
  expiresAt: z.number().optional(),
  username: z.string().optional(),
  userId: z.string().optional(),
  workspaceSlug: z.string().optional(),
});
export type BitbucketConfig = z.infer<typeof BitbucketConfigSchema>;

const NotionSyncSettingsSchema = z.object({
  includeComments: z.boolean(),
  includeDatabases: z.boolean(),
  autoMerge: z.boolean(),
  branchPrefix: z.string(),
  defaultDestination: z.string().optional(),
  operatingMode: z.enum(['mirror', 'adopt', 'bidirectional']),
  autoCreateTopics: z.boolean(),
  preserveHierarchy: z.boolean(),
});

/** Notion credentials and the legacy document settings retained by its service. */
export const NotionConfigSchema = z.object({
  authMode: z.literal('oauth').optional(),
  grantId: z.string().uuid().optional(),
  oauthAppFingerprint: z.string().min(1).optional(),
  accessToken: z.string().trim().min(1),
  refreshToken: z.string().trim().min(1).optional(),
  workspaceId: z.string().trim().min(1),
  workspaceName: z.string().optional(),
  botId: z.string().optional(),
  webhookSecret: z.string().optional(),
  webhookId: z.string().optional(),
  webhookUrl: z.string().optional(),
  syncSettings: NotionSyncSettingsSchema.optional(),
});
export type NotionConfig = z.infer<typeof NotionConfigSchema>;

/** Normalize the previous callback shape at the shared credential boundary. */
export const ConfluenceConfigSchema = z.object({
  authMode: z.literal('oauth').optional(),
  grantId: z.string().uuid().optional(),
  oauthAppFingerprint: z.string().min(1).optional(),
  token: z.string().trim().min(1).optional(),
  accessToken: z.string().trim().min(1).optional(),
  refreshToken: z.string().trim().min(1).optional(),
  expiresAt: z.number().finite().positive().optional(),
  site: z.object({
    id: z.string().regex(/^[A-Za-z0-9-]+$/),
    url: z.string().url().optional(), name: z.string().optional(),
  }).optional(),
  cloudId: z.string().regex(/^[A-Za-z0-9-]+$/).optional(),
  url: z.string().url().optional(),
  siteUrl: z.string().url().optional(),
  siteName: z.string().optional(),
  userEmail: z.string().optional(),
}).superRefine((value, ctx) => {
  if ((!value.token && !value.accessToken) || (!value.site && !value.cloudId)
    || (value.token && value.accessToken && value.token !== value.accessToken)
    || (value.site && value.cloudId && value.site.id !== value.cloudId)
    || (value.url && value.siteUrl && value.url !== value.siteUrl)
    || (value.site?.url && (value.siteUrl ?? value.url) && value.site.url !== (value.siteUrl ?? value.url))) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Confluence credentials are missing or conflicting. Reconnect Confluence.' });
  }
}).transform(({ accessToken, cloudId, url, siteUrl, siteName, ...value }) => ({
  ...value,
  token: (value.token ?? accessToken)!,
  site: value.site ?? { id: cloudId!, url: siteUrl ?? url, name: siteName },
}));
export type ConfluenceConfig = z.infer<typeof ConfluenceConfigSchema>;

export const SnowflakeConfigSchema = z.object({
  authMode: z.literal('oauth'),
  accountUrl: z.string().url(),
  role: z.string().regex(/^[A-Z_][A-Z0-9_]{0,254}$/),
  warehouse: z.string().trim().min(1).max(255),
  accessToken: z.string().regex(/^[\x21-\x7e]+$/),
  refreshToken: z.string().regex(/^[\x21-\x7e]+$/).optional(),
  expiresAt: z.number().finite().positive().optional(),
  providerAccount: z.string().min(1).optional(),
  oauthWorkspaceId: z.string().min(1).optional(),
  oauthAppFingerprint: z.string().min(1).optional(),
  grantId: z.string().uuid().optional(),
});
export type SnowflakeConfig = z.infer<typeof SnowflakeConfigSchema>;

/**
 * Linear OAuth configuration
 */
export const LinearConfigSchema = z.object({
  token: z.string(),
  refreshToken: z.string().optional(),
  expiresAt: z.number().optional(),
  organizationId: z.string().optional(),
  workspaceName: z.string().optional(),
  apiUrl: z.string().optional(),
  userId: z.string().optional(),
  userEmail: z.string().optional(),
});
export type LinearConfig = z.infer<typeof LinearConfigSchema>;

/**
 * Intercom OAuth configuration
 */
export const IntercomConfigSchema = z.object({
  token: z.string(),
  refreshToken: z.string().optional(),
  expiresAt: z.number().optional(),
  workspaceId: z.string().optional(),
  workspaceName: z.string().optional(),
  userId: z.string().optional(),
  userEmail: z.string().optional(),
  appId: z.string().optional(),
});
export type IntercomConfig = z.infer<typeof IntercomConfigSchema>;

/** Freshdesk v2 API-key credentials; legacy OAuth configurations require reconnect. */
export const FreshdeskConfigSchema = z.object({
  authMode: z.literal('api_key'),
  apiKey: z.string().min(1).max(4096).regex(/^[A-Za-z0-9_-]+$/),
  domain: z.string().regex(/^[a-z0-9][a-z0-9-]*\.freshdesk\.com$/i).transform(value => value.toLowerCase()),
  ticketIds: z.array(z.string().regex(/^[1-9][0-9]*$/).refine(value => Number.isSafeInteger(Number(value)))).max(100).default([]),
  userId: z.string().regex(/^[1-9][0-9]*$/).optional(),
});
export type FreshdeskConfig = z.infer<typeof FreshdeskConfigSchema>;

/**
 * Jira OAuth configuration
 */
export const JiraConfigSchema = z.object({
  token: z.string(),
  refreshToken: z.string().optional(),
  expiresAt: z.number().optional(),
  cloudId: z.string(),
  site: z.object({
    id: z.string(),
    url: z.string(),
    name: z.string().optional(),
  }).optional(),
  userEmail: z.string().optional(),
  userId: z.string().optional(),
});
export type JiraConfig = z.infer<typeof JiraConfigSchema>;

/**
 * Slack OAuth configuration
 */
export const SlackConfigSchema = z.object({
  token: z.string(),
  teamId: z.string(),
  teamName: z.string().optional(),
  botUserId: z.string().optional(),
  webhookUrl: z.string().optional(),
});
export type SlackConfig = z.infer<typeof SlackConfigSchema>;

/**
 * Microsoft Teams OAuth configuration
 */
export const MicrosoftTeamsConfigSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string().optional(),
  expiresAt: z.number().optional(),
  tenantId: z.string(),
  teamId: z.string().optional(),
  teamName: z.string().optional(),
  botId: z.string().optional(),
  webhookUrl: z.string().optional(),
});
export type MicrosoftTeamsConfig = z.infer<typeof MicrosoftTeamsConfigSchema>;

/**
 * Google Drive OAuth configuration
 */
export const GoogleDriveConfigSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string().optional(),
  expiresAt: z.number().optional(),
  userEmail: z.string().optional(),
});
export type GoogleDriveConfig = z.infer<typeof GoogleDriveConfigSchema>;

/**
 * Zendesk OAuth configuration
 */
export const ZendeskConfigSchema = z.object({
  token: z.string(),
  accessToken: z.string().optional(), // OAuth access token (same as token, for compatibility)
  tokenType: z.string().optional(), // OAuth token type (usually 'Bearer')
  scope: z.string().optional(), // OAuth scope
  refreshToken: z.string().optional(),
  expiresAt: z.number().optional(),
  subdomain: z.string(),
  userEmail: z.string().optional(),
  userId: z.union([z.string(), z.number()]).optional(),
});
export type ZendeskConfig = z.infer<typeof ZendeskConfigSchema>;

/**
 * Local file system configuration
 */
export const LocalFileConfigSchema = z.object({
  rootPath: z.string(),
  watchEnabled: z.boolean().optional(),
});
export type LocalFileConfig = z.infer<typeof LocalFileConfigSchema>;

/**
 * External documentation configuration
 */
export const ExternalDocsConfigSchema = z.object({
  sourceType: z.enum(['web', 'filesystem', 'github']),
  url: z.string().optional(),
  rootPath: z.string().optional(),
  repository: z.string().optional(),
  branch: z.string().optional(),
});
export type ExternalDocsConfig = z.infer<typeof ExternalDocsConfigSchema>;

/**
 * Microsoft Intune configuration (Azure AD OAuth)
 */
export const MicrosoftIntuneConfigSchema = z.object({
  tenantId: z.string().uuid('Tenant ID must be a valid UUID'),
  clientId: z.string(),
  clientSecret: z.string(),
  accessToken: z.string().optional(),
  refreshToken: z.string().optional(),
  expiresAt: z.number().optional(),
});
export type MicrosoftIntuneConfig = z.infer<typeof MicrosoftIntuneConfigSchema>;

/**
 * FleetDM configuration (API token)
 */
export const FleetDMConfigSchema = z.object({
  serverUrl: z.string(),
  apiToken: z.string(),
});
export type FleetDMConfig = z.infer<typeof FleetDMConfigSchema>;

/**
 * Jamf Pro configuration (OAuth client credentials)
 */
export const JamfProConfigSchema = z.object({
  serverUrl: z.string(),
  clientId: z.string(),
  clientSecret: z.string(),
  accessToken: z.string().optional(),
  expiresAt: z.number().optional(),
});
export type JamfProConfig = z.infer<typeof JamfProConfigSchema>;

// ============================================
// Integration Metadata Types (User Settings)
// ============================================

/**
 * Common sync settings used by multiple integrations
 */
export const SyncSettingsSchema = z.object({
  autoSync: z.boolean().optional(),
  syncInterval: z.number().optional(),
  lastSyncedAt: z.union([z.date(), z.string()]).optional(),
  syncDirection: z.enum(['import', 'export', 'bidirectional']).optional(),
});
export type SyncSettings = z.infer<typeof SyncSettingsSchema>;

/**
 * Notion-specific metadata
 */
export const NotionMetadataSchema = SyncSettingsSchema.extend({
  features: z.array(z.string()).optional(),
  syncSettings: NotionSyncSettingsSchema.optional(),
  userInfo: z.any().optional(),
});
export type NotionMetadata = z.infer<typeof NotionMetadataSchema>;

/**
 * Confluence-specific metadata
 */
export const ConfluenceMetadataSchema = SyncSettingsSchema.extend({
  features: z.array(z.string()).optional(),
  spaces: z.array(z.string()).optional(),
  defaultSpaceKey: z.string().optional(),
  userInfo: z.any().optional(),
});
export type ConfluenceMetadata = z.infer<typeof ConfluenceMetadataSchema>;

/**
 * Linear-specific metadata
 */
export const LinearMetadataSchema = SyncSettingsSchema.extend({
  features: z.array(z.string()).optional(),
  teamIds: z.array(z.string()).optional(),
  defaultTeamId: z.string().optional(),
  workflowStates: z.array(z.string()).optional(),
  lastIssueSyncAt: z.union([z.date(), z.string()]).optional(),
  userInfo: z.any().optional(),
});
export type LinearMetadata = z.infer<typeof LinearMetadataSchema>;

/**
 * Intercom-specific metadata
 */
export const IntercomMetadataSchema = SyncSettingsSchema.extend({
  features: z.array(z.string()).optional(),
  inboxIds: z.array(z.string()).optional(),
  lastConversationSyncAt: z.union([z.date(), z.string()]).optional(),
  userInfo: z.any().optional(),
});
export type IntercomMetadata = z.infer<typeof IntercomMetadataSchema>;

/**
 * Freshdesk-specific metadata
 */
export const FreshdeskMetadataSchema = SyncSettingsSchema.extend({
  features: z.array(z.string()).optional(),
  groupIds: z.array(z.string()).optional(),
  agentIds: z.array(z.string()).optional(),
  lastTicketSyncAt: z.union([z.date(), z.string()]).optional(),
  userInfo: z.any().optional(),
});
export type FreshdeskMetadata = z.infer<typeof FreshdeskMetadataSchema>;

/**
 * Jira-specific metadata
 */
export const JiraMetadataSchema = SyncSettingsSchema.extend({
  features: z.array(z.string()).optional(),
  projectKeys: z.array(z.string()).optional(),
  defaultJql: z.string().optional(),
  lastIssueSyncAt: z.union([z.date(), z.string()]).optional(),
  userInfo: z.any().optional(),
});
export type JiraMetadata = z.infer<typeof JiraMetadataSchema>;

/**
 * Zendesk-specific metadata
 */
export const ZendeskMetadataSchema = SyncSettingsSchema.extend({
  features: z.array(z.string()).optional(),
  zendeskConfig: z.object({
    publishMode: z.enum(['all', 'selected', 'manual']).optional(),
    flattenSections: z.boolean().optional(),
    sectionNamingStrategy: z.enum(['breadcrumb', 'path']).optional(),
    defaultCategoryId: z.string().optional(),
    defaultSectionId: z.string().optional(),
    syncMode: z.enum(['publish', 'import']).optional(),
    importMode: z.enum(['all', 'category']).optional(),
    importCategoryId: z.string().optional(),
  }).optional(),
  userInfo: z.any().optional(),
  locale: z.string().optional(),
  timeZone: z.string().optional(),
});
export type ZendeskMetadata = z.infer<typeof ZendeskMetadataSchema>;

/**
 * Google Drive-specific metadata
 */
export const GoogleDriveMetadataSchema = SyncSettingsSchema.extend({
  features: z.array(z.string()).optional(),
  folderId: z.string().optional(),
  mimeTypes: z.array(z.string()).optional(),
});
export type GoogleDriveMetadata = z.infer<typeof GoogleDriveMetadataSchema>;

/**
 * GitHub-specific metadata
 */
export const GitHubMetadataSchema = SyncSettingsSchema.extend({
  selectedRepository: z.string().optional(),
  defaultBranch: z.string().optional(),
  paths: z.array(z.string()).optional(),
});
export type GitHubMetadata = z.infer<typeof GitHubMetadataSchema>;

/**
 * Bitbucket-specific metadata
 */
export const BitbucketMetadataSchema = SyncSettingsSchema.extend({
  features: z.array(z.string()).optional(),
  selectedRepository: z.string().optional(),
  defaultBranch: z.string().optional(),
  paths: z.array(z.string()).optional(),
  workspaceSlug: z.string().optional(),
});
export type BitbucketMetadata = z.infer<typeof BitbucketMetadataSchema>;

/**
 * GitLab-specific config
 */
export const GitLabConfigSchema = z.object({
  token: z.string(),
  refreshToken: z.string().optional(),
  expiresAt: z.number().optional(),
  username: z.string().optional(),
  userId: z.number().optional(),
  instanceUrl: z.string().optional(), // For self-hosted GitLab instances
});
export type GitLabConfig = z.infer<typeof GitLabConfigSchema>;

/**
 * GitLab-specific metadata
 */
export const GitLabMetadataSchema = SyncSettingsSchema.extend({
  features: z.array(z.string()).optional(),
  selectedProject: z.string().optional(),
  defaultBranch: z.string().optional(),
  paths: z.array(z.string()).optional(),
  groupId: z.number().optional(),
  instanceUrl: z.string().optional(), // For self-hosted GitLab instances
  instanceVersion: z.string().optional(),
});
export type GitLabMetadata = z.infer<typeof GitLabMetadataSchema>;

/**
 * Slack-specific metadata
 */
export const SlackMetadataSchema = SyncSettingsSchema.extend({
  channels: z.array(z.string()).optional(),
  defaultChannel: z.string().optional(),
  notificationSettings: z.object({
    onPublish: z.boolean().optional(),
    onUpdate: z.boolean().optional(),
    onComment: z.boolean().optional(),
  }).optional(),
});
export type SlackMetadata = z.infer<typeof SlackMetadataSchema>;

/**
 * Microsoft Teams-specific metadata
 */
export const MicrosoftTeamsMetadataSchema = SyncSettingsSchema.extend({
  teams: z.array(z.string()).optional(),
  channels: z.array(z.string()).optional(),
  defaultTeam: z.string().optional(),
  defaultChannel: z.string().optional(),
  notificationSettings: z.object({
    onPublish: z.boolean().optional(),
    onUpdate: z.boolean().optional(),
    onComment: z.boolean().optional(),
  }).optional(),
}).catchall(z.any());
export type MicrosoftTeamsMetadata = z.infer<typeof MicrosoftTeamsMetadataSchema>;

/**
 * MDM-specific metadata (shared by Intune, FleetDM, Jamf)
 */
export const MdmMetadataSchema = SyncSettingsSchema.extend({
  features: z.array(z.string()).optional(),
  lastScanAt: z.union([z.date(), z.string()]).optional(),
  deviceCount: z.number().optional(),
  complianceRate: z.number().optional(),
});
export type MdmMetadata = z.infer<typeof MdmMetadataSchema>;

/**
 * Generic metadata for integrations without specific settings
 */
export const GenericMetadataSchema = SyncSettingsSchema.extend({
  features: z.array(z.string()).optional(),
}).catchall(z.any());
export type GenericMetadata = z.infer<typeof GenericMetadataSchema>;

// ============================================
// Type Mapping for Integrations
// ============================================

/**
 * Map of integration types to their config types
 */
export interface IntegrationConfigMap {
  GitHub: GitHubConfig;
  Bitbucket: BitbucketConfig;
  GitLab: GitLabConfig;
  Notion: NotionConfig;
  Confluence: ConfluenceConfig;
  Linear: LinearConfig;
  Intercom: IntercomConfig;
  Freshdesk: FreshdeskConfig;
  Jira: JiraConfig;
  Slack: SlackConfig;
  MicrosoftTeams: MicrosoftTeamsConfig;
  GoogleDrive: GoogleDriveConfig;
  Zendesk: ZendeskConfig;
  LocalFile: LocalFileConfig;
  Docusaurus: ExternalDocsConfig;
  VitePress: ExternalDocsConfig;
  GitBook: ExternalDocsConfig;
  MicrosoftIntune: MicrosoftIntuneConfig;
  FleetDM: FleetDMConfig;
  JamfPro: JamfProConfig;
}

/**
 * Map of integration types to their metadata types
 */
export interface IntegrationMetadataMap {
  GitHub: GitHubMetadata;
  Bitbucket: BitbucketMetadata;
  Notion: NotionMetadata;
  Confluence: ConfluenceMetadata;
  Linear: LinearMetadata;
  Intercom: IntercomMetadata;
  Freshdesk: FreshdeskMetadata;
  Jira: JiraMetadata;
  Slack: SlackMetadata;
  MicrosoftTeams: MicrosoftTeamsMetadata;
  GoogleDrive: GoogleDriveMetadata;
  Zendesk: ZendeskMetadata;
  LocalFile: GenericMetadata;
  Docusaurus: GenericMetadata;
  VitePress: GenericMetadata;
  GitBook: GenericMetadata;
  MicrosoftIntune: MdmMetadata;
  FleetDM: MdmMetadata;
  JamfPro: MdmMetadata;
}

/**
 * Union type of all config schemas
 */
export const IntegrationConfigSchema = z.union([
  GitHubConfigSchema,
  BitbucketConfigSchema,
  NotionConfigSchema,
  ConfluenceConfigSchema,
  LinearConfigSchema,
  IntercomConfigSchema,
  FreshdeskConfigSchema,
  JiraConfigSchema,
  SlackConfigSchema,
  MicrosoftTeamsConfigSchema,
  GoogleDriveConfigSchema,
  ZendeskConfigSchema,
  LocalFileConfigSchema,
  ExternalDocsConfigSchema,
  MicrosoftIntuneConfigSchema,
  FleetDMConfigSchema,
  JamfProConfigSchema,
]);

/**
 * Union type of all metadata schemas
 */
export const IntegrationMetadataSchema = z.union([
  NotionMetadataSchema,
  ConfluenceMetadataSchema,
  LinearMetadataSchema,
  IntercomMetadataSchema,
  FreshdeskMetadataSchema,
  JiraMetadataSchema,
  ZendeskMetadataSchema,
  GoogleDriveMetadataSchema,
  GitLabMetadataSchema,
  BitbucketMetadataSchema,
  GitHubMetadataSchema,
  SlackMetadataSchema,
  MicrosoftTeamsMetadataSchema,
  MdmMetadataSchema,
  GenericMetadataSchema,
]);

export type JiraConfigInput = z.input<typeof JiraConfigSchema>;
export type JiraMetadataInput = z.input<typeof JiraMetadataSchema>;
export type LinearConfigInput = z.input<typeof LinearConfigSchema>;
export type LinearMetadataInput = z.input<typeof LinearMetadataSchema>;
export type IntercomConfigInput = z.input<typeof IntercomConfigSchema>;
export type IntercomMetadataInput = z.input<typeof IntercomMetadataSchema>;
export type FreshdeskConfigInput = z.input<typeof FreshdeskConfigSchema>;
export type FreshdeskMetadataInput = z.input<typeof FreshdeskMetadataSchema>;
export type MicrosoftTeamsConfigInput = z.input<typeof MicrosoftTeamsConfigSchema>;
export type MicrosoftTeamsMetadataInput = z.input<typeof MicrosoftTeamsMetadataSchema>;
export type BitbucketConfigInput = z.input<typeof BitbucketConfigSchema>;
export type BitbucketMetadataInput = z.input<typeof BitbucketMetadataSchema>;
export const BitbucketMetadataInputSchema = BitbucketMetadataSchema;
export type GitLabConfigInput = z.input<typeof GitLabConfigSchema>;
export type GitLabMetadataInput = z.input<typeof GitLabMetadataSchema>;
export const GitLabMetadataInputSchema = GitLabMetadataSchema;
